/**
 * Saker: what needs the manager in the period on screen (issue #147), in three levels, as the owner's
 * prototype «Saker navigation redesign» (case-templates.prototype.html, 2026-10-28) draws it: an
 * overview (tabs Å følge opp, Venter, Utsatt; a search; Haster nå; the work areas with their counts),
 * one area's list, and one case (CaseDetail) with a pager scoped to that list. Beside the week it is
 * the side column ('panel'); on a phone a closed summary opens the same levels as a sheet ('bar').
 *
 * Presentation only: it draws the cases it is given (reviewBar.types.ts), reports which action was
 * taken, and draws the outcome the page hands back. Stepping never changes anything; only an action
 * does, and a case moves on only when the page says it was saved. Styles: ReviewBar.module.css.
 */
import { T } from '@/i18n';
import { askBeforeLeaving } from '@/shell/context';
import { Drawer, toast } from '@/ui';
import { Icon, type IconName } from '@/ui/icons';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CaseDetail, stateOf } from './CaseDetail';
import css from './ReviewBar.module.css';
import type { CaseAction, CaseCat, CaseOutcome, ReviewCase, SourceGap } from './reviewBar.types';

export interface ReviewBarProps {
  cases: ReviewCase[];
  /** waiting on an employee: the Venter tab, never counted as work */
  waiting: ReviewCase[];
  /** "1 nå · 3 forespørsler · …", on the phone's closed summary */
  breakdown: string;
  /** what the counts cover, said as it is: the dates read, and the period on screen (PR #150, F3) */
  scope: string;
  /** reads that have not answered or failed, with the categories they feed: not known clear (RF1) */
  gaps: SourceGap[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  /** run an action; the bar shows the outcome and moves on only when it says saved */
  onAct: (
    c: ReviewCase,
    a: CaseAction,
    input?: { at: string; reason: string; approve: boolean },
  ) => Promise<CaseOutcome>;
  onLink: (to: string) => void;
  /** a shift in the side panel, on its clock times */
  onShift?: (shiftId: number) => void;
  /** a week item's way in: its shift, day or week rule */
  onOpen: (item: string) => void;
  /** what the page adds under a case's actions: Hugin's word on it (#131) */
  extra?: ((c: ReviewCase) => ReactNode) | undefined;
  /** what answering does: an absence's deduction, balance and shifts (review of #147, F2) */
  impact?: ((c: ReviewCase) => ReactNode) | undefined;
  /** more for the case's description: how an absence's deduction is counted */
  about?: ((c: ReviewCase) => ReactNode) | undefined;
  onPerson: (id: number) => void;
  onDay: (date: string) => void;
  /** the case on screen, for the plan to mark its shift; null when none is open */
  onActive: (c: ReviewCase | null) => void;
  /** the open cases dated outside the period on screen, left out of the list (owner 18.10) */
  elsewhere?: number;
  /** the way to them all: Oversikt → Oppfølging */
  onElsewhere?: () => void;
  /** whether a case's date is outside the period the plan shows */
  outside: (c: ReviewCase) => boolean;
  onShowPeriod: (c: ReviewCase) => void;
  /** open on the first case from the start (fixtures, a deep link) */
  defaultOpen?: boolean;
  /**
   * A card's dot or a warning's count (#146): these cases, named by `label`. One case opens; more are
   * listed. `n` changes on every press, so the same scope asked twice opens again.
   */
  focus?: { label: string; keys: string[]; n: number } | null | undefined;
  /** the keys Saker is filtered to, or null: the page outlines their shifts (owner 2026-10-25) */
  onScope?: ((keys: string[] | null) => void) | undefined;
  /** 'panel' the side column beside the week; 'bar' the phone's summary, the levels in a sheet */
  layout?: 'bar' | 'panel';
  /** ask Hugin about an area's cases; the words go to the docked chat */
  onAskHugin?: ((text: string) => void) | undefined;
  /** the company, so what was put off with Ta senere is kept per company */
  laterKey?: string | undefined;
}

type Bucket = 'active' | 'waiting' | 'later';
/** the list level: one work area, or the cases a card's dot or a warning asked for */
type List = { kind: 'cat'; cat: CaseCat } | { kind: 'scope'; label: string; keys: string[] };

/** The work areas, in the prototype's words (DOMAINS); neutral icons, the count the only figure. */
const AREAS: { c: CaseCat; name: string; sub: string; icon: IconName }[] = [
  { c: 'na', name: 'Stempling', sub: 'Manglende tider og registreringer', icon: 'clock' },
  { c: 'foresporsler', name: 'Forespørsler', sub: 'Fravær, ledige vakter og bytter', icon: 'chat' },
  { c: 'timer', name: 'Timer', sub: 'Registreringer før lønn', icon: 'check' },
  { c: 'planlegg', name: 'Vaktplan', sub: 'Bemanning og varsler i planen', icon: 'calendar' },
  { c: 'lonn', name: 'Lønn', sub: 'Ansattopplysninger og lønnsperioder', icon: 'banknote' },
];
const areaName = (c: CaseCat) => T(AREAS.find((a) => a.c === c)?.name ?? '');

const DAY = 864e5;
/**
 * Haster nå (the prototype's rule): the case's shift is running or starts within 24 hours. Who it
 * waits on and Ta senere are the caller's: only the manager's own, not put off, are asked.
 */
export function urgent(c: ReviewCase, now: number): boolean {
  if (!c.span) return false;
  return Date.parse(c.span.start) - DAY <= now && Date.parse(c.span.end) > now;
}
/** what a search matches: the person, the case's words, its date and its area */
const matches = (c: ReviewCase, q: string) =>
  [c.title, c.what, c.description, c.person?.name, c.dateLabel, c.dateText, areaName(c.cat)]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('nb')
    .includes(q.toLocaleLowerCase('nb'));

// ponytail: Ta senere has no server side (no snooze field on any case source), so what was put off is
// kept in sessionStorage per company and per browser tab, with no timed return; a snooze column is the upgrade
const laterStore = (k: string | undefined) => `timefly-saker-later-${k ?? ''}`;
function readLater(k: string | undefined): string[] {
  try {
    const v = JSON.parse(sessionStorage.getItem(laterStore(k)) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

const count = (n: number) => (n === 1 ? T('1 sak å se over') : T('{n} saker å se over', { n }));

export function ReviewBar(p: ReviewBarProps) {
  const panel = p.layout === 'panel';
  // the phone's sheet; the side column is always open
  const [open, setOpen] = useState(!!p.defaultOpen);
  const [bucket, setBucket] = useState<Bucket>('active');
  const [list, setList] = useState<List | null>(() =>
    p.defaultOpen && p.cases[0] ? { kind: 'cat', cat: p.cases[0].cat } : null,
  );
  const [activeKey, setActiveKey] = useState<string | null>(p.defaultOpen ? (p.cases[0]?.key ?? null) : null);
  const [search, setSearch] = useState('');
  const [allUrgent, setAllUrgent] = useState(false);
  const [later, setLaterState] = useState(() => readLater(p.laterKey));
  useEffect(() => setLaterState(readLater(p.laterKey)), [p.laterKey]);
  const setLater = (next: string[]) => {
    setLaterState(next);
    try {
      sessionStorage.setItem(laterStore(p.laterKey), JSON.stringify(next));
    } catch {}
  };
  const [mode, setMode] = useState<'actions' | 'form'>('actions');
  const [busy, setBusy] = useState(false);
  // Finn erstatter is open in the page's panel: the case waits for its answer (candidate addendum, AI-02)
  const [parked, setParked] = useState<string | null>(null);
  // a save in flight holds the review where it is: no stepping, no level change, no closing (F5)
  const busyRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Extract<CaseOutcome, { kind: 'partial' }> | null>(null);
  const [gone, setGone] = useState<string | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const headRef = useRef<HTMLHeadingElement>(null);
  const rootRef = useRef<HTMLElement | null>(null);

  const byKey = useMemo(
    () => new Map([...p.cases, ...p.waiting].map((c) => [c.key, c])),
    [p.cases, p.waiting],
  );
  const laterSet = new Set(later);
  const mine = p.cases.filter((c) => !laterSet.has(c.key));
  const postponed = p.cases.filter((c) => laterSet.has(c.key));
  const pool = bucket === 'waiting' ? p.waiting : bucket === 'later' ? postponed : mine;
  const bucketOf = (c: ReviewCase): Bucket =>
    p.waiting.includes(c) ? 'waiting' : laterSet.has(c.key) ? 'later' : 'active';
  // the list the pager steps through
  const shown =
    list?.kind === 'scope'
      ? [...p.cases, ...p.waiting].filter((c) => list.keys.includes(c.key))
      : list
        ? pool.filter((c) => c.cat === list.cat)
        : [];
  const listName = list ? (list.kind === 'scope' ? list.label : areaName(list.cat)) : '';
  // the case on screen, read after an await: an outcome belongs to the case that started it (F5)
  const activeRef = useRef<string | null>(null);
  const active = activeKey ? byKey.get(activeKey) : undefined;
  const at = active ? shown.findIndex((c) => c.key === active.key) : -1;
  activeRef.current = activeKey;
  const visible = panel || open;

  // the plan marks the case on screen
  // biome-ignore lint/correctness/useExhaustiveDependencies: tell the page when the case changes, not on every render
  useEffect(() => {
    p.onActive(visible && active ? active : null);
  }, [visible, active?.key]);
  // the cases a card's dot or a warning listed: the plan outlines their shifts
  const scopeKeys = list?.kind === 'scope' && visible ? list.keys.join() : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the keys are the signal, the callback may change
  useEffect(() => {
    p.onScope?.(list?.kind === 'scope' && visible ? list.keys : null);
    return () => p.onScope?.(null);
  }, [scopeKeys]);

  // a case that went away while it was on screen: resolved or changed somewhere else (UC12, UC13)
  useEffect(() => {
    if (activeKey && !byKey.has(activeKey) && gone !== activeKey) setGone(activeKey);
  }, [activeKey, byKey, gone]);

  const reset = () => {
    setMode('actions');
    setError(null);
  };
  // a correction typed and not saved: stepping or leaving the case asks first (review of #147, item 4)
  const onDirty = useCallback((d: boolean) => setMode(d ? 'form' : 'actions'), []);
  const go = (key: string | null) => {
    if (busyRef.current) return;
    reset();
    setGone(null);
    setActiveKey(key);
    requestAnimationFrame(() => (key ? titleRef : headRef).current?.focus({ preventScroll: true }));
  };
  /** Moving away from a half-typed correction asks first, with the app's own leave question. */
  const away = (label: string, f: () => void) =>
    mode === 'form' ? askBeforeLeaving(label, f, { discard: reset }) : f();
  const openCase = (c: ReviewCase, from?: List) => {
    if (busyRef.current) return;
    setBucket(bucketOf(c));
    setList(from ?? { kind: 'cat', cat: c.cat });
    setSearch('');
    setReceipt(null);
    go(c.key);
  };
  const openList = (l: List | null) => {
    if (busyRef.current) return;
    away(T('Annen sak'), () => {
      setList(l);
      go(null);
    });
  };
  const back = () => (active || gone ? openList(list) : openList(null));
  const step = (d: 1 | -1) => {
    if (busyRef.current || at < 0) return;
    const next = shown[at + d];
    if (next) away(T('Annen sak'), () => go(next.key));
  };
  const pickBucket = (b: Bucket) => {
    if (busyRef.current) return;
    setBucket(b);
    setList(null);
    setAllUrgent(false);
    go(null);
  };

  // a card's dot or a warning's count: one case opens, more are listed (prototype; owner 2026-10-25)
  // A save in flight is never interrupted; the press is then ignored, as stepping is.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only a new press opens
  useEffect(() => {
    const f = p.focus;
    if (!f || busyRef.current) return;
    setOpen(true);
    setSearch('');
    setReceipt(null);
    const one = f.keys.length === 1 ? byKey.get(f.keys[0] as string) : undefined;
    if (one) openCase(one);
    else {
      setBucket('active');
      setList({ kind: 'scope', label: f.label, keys: f.keys });
      go(null);
    }
    requestAnimationFrame(() => rootRef.current?.scrollIntoView?.({ block: 'nearest' }));
  }, [p.focus?.n]);

  // J next, K previous in a case; never while typing, never with a modifier (UC3). Escape goes up a level.
  useEffect(() => {
    if (!visible) return;
    const f = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (busyRef.current) return;
      if (panel && e.key === 'Escape' && rootRef.current?.contains(t) && (list || active)) {
        e.preventDefault();
        back();
        return;
      }
      if (!active || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (t?.closest('input, select, textarea, [contenteditable="true"]')) return;
      if (e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        step(1);
      } else if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        step(-1);
      }
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  });

  const act = async (
    c: ReviewCase,
    a: CaseAction,
    input?: { at: string; reason: string; approve: boolean },
  ) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    // where the case stood when the action started: the next one is worked out from here
    const from = shown;
    const fromAt = at;
    if (a.kind === 'cover') setParked(c.title);
    let r: CaseOutcome;
    try {
      r = await p.onAct(c, a, input);
    } finally {
      busyRef.current = false;
      setBusy(false);
      setParked(null);
    }
    // an outcome is only ever shown on its own case
    if (activeRef.current !== c.key) return;
    if (r.kind === 'saved') {
      toastSaved(r);
      // the next case in the list opens only after the save is confirmed (UC5); none left, the list
      const rest = from.filter((x) => x.key !== c.key);
      go(rest[fromAt < 0 ? 0 : Math.min(fromAt, rest.length - 1)]?.key ?? null);
    } else if (r.kind === 'partial') {
      setReceipt(r);
      reset();
    } else if (r.kind === 'refused') setError(r.message);
    else if (r.kind === 'cancelled')
      return; // nothing was sent; the case stands as it was
    else setError(T('Vi vet ikke om endringen ble lagret. Planen leses på nytt før du gjør noe mer.'));
  };

  /** a saved outcome as a toast; its Angre holds the review as a save does, and a failed undo stays as a bar */
  function toastSaved(r: Extract<CaseOutcome, { kind: 'saved' }>) {
    const undo = r.undo;
    toast(
      r.detail ? `${r.text}. ${r.detail}` : r.text,
      undo
        ? () =>
            void (async () => {
              if (busyRef.current) return;
              busyRef.current = true;
              setBusy(true);
              let u: CaseOutcome;
              try {
                u = await undo();
              } finally {
                busyRef.current = false;
                setBusy(false);
              }
              if (u.kind === 'saved') toast(u.detail ? `${u.text}. ${u.detail}` : u.text);
              else
                setReceipt({
                  kind: 'partial',
                  text: r.text,
                  message:
                    u.kind === 'refused'
                      ? T('Angring feilet: {m}', { m: u.message })
                      : T('Vi vet ikke om angringen ble lagret.'),
                });
            })()
        : undefined,
    );
  }

  /** Ta senere: the case goes to Utsatt and the next one opens; in Utsatt it is brought back */
  const onLater = (c: ReviewCase) => {
    if (busyRef.current) return;
    if (laterSet.has(c.key)) {
      setLater(later.filter((k) => k !== c.key));
      setBucket('active');
      return;
    }
    const rest = shown.filter((x) => x.key !== c.key);
    const next = rest[Math.min(Math.max(at, 0), rest.length - 1)] ?? null;
    away(T('Annen sak'), () => {
      setLater([...later, c.key]);
      go(next?.key ?? null);
      toast(T('Saken er flyttet til Utsatt'), () => setLater(later));
    });
  };

  if (p.failed && !p.cases.length)
    return (
      <section className={css.bar} data-testid="review" aria-label={T('Saker å se over')}>
        <div className={css.summary}>
          <Icon name="warn" size={18} strokeWidth={1.7} />
          <div className={css.summaryText}>
            {/* a failed read is not "nothing to do" */}
            <strong>{T('Kunne ikke hente sakene')}</strong>
            <p>{T('Det betyr ikke at det ikke er noe å se over.')}</p>
          </div>
          <button type="button" className={css.link} onClick={p.onRetry}>
            {T('Prøv igjen')}
          </button>
        </div>
      </section>
    );

  const total = p.cases.length;
  const failedGaps = p.gaps.filter((g) => g.state === 'failed');
  /** what is not known about an area: a read failed, or has not answered; null when all did (RF1) */
  const gapOf = (c: CaseCat | null): 'failed' | 'loading' | null => {
    const g = p.gaps.filter((x) => c == null || x.cats.includes(c));
    return g.some((x) => x.state === 'failed') ? 'failed' : g.length ? 'loading' : null;
  };
  const known = gapOf(null);
  const now = Date.now();

  const row = (c: ReviewCase, withArea = false, from?: List) => {
    const s = stateOf(c, { waiting: bucket === 'waiting', later: laterSet.has(c.key) });
    // the manager's own cases are the tab's default: their tag is left out there (prototype row())
    const tag = bucket !== 'active' || s.word !== T('Venter på deg');
    return (
      <li key={c.key}>
        <button
          type="button"
          className={css.caseRow}
          data-testid="review-row"
          data-key={c.key}
          aria-current={c.key === activeKey ? 'true' : undefined}
          onClick={() => openCase(c, from)}
        >
          <span className={css.rowCopy}>
            <span className={css.rowTitle}>{c.title}</span>
            {c.person && <span className={css.rowPerson}>{c.person.name}</span>}
            <span className={css.rowMeta}>
              {[c.dateLabel ?? c.when, withArea ? areaName(c.cat) : ''].filter(Boolean).join(', ')}
            </span>
            {tag && (
              <span className={css.tag} data-state={s.kind}>
                {s.word}
              </span>
            )}
          </span>
          <Icon name="chevronRight" size={16} />
        </button>
      </li>
    );
  };
  const empty = (title: string, text: string) => (
    <div className={css.vempty} data-testid="review-empty">
      <h3>
        <Icon name="check" size={20} />
        {title}
      </h3>
      <p>{text}</p>
    </div>
  );

  const overview = () => {
    const found = search ? pool.filter((c) => matches(c, search)) : [];
    const urg = bucket === 'active' ? mine.filter((c) => urgent(c, now)) : [];
    const areas = AREAS.filter((a) => pool.some((c) => c.cat === a.c) || (bucket === 'active' && gapOf(a.c)));
    return (
      <>
        <div className={css.vhead}>
          <h2 ref={headRef} tabIndex={-1} data-testid="review-total">
            {bucket === 'waiting'
              ? T('Venter på andre')
              : bucket === 'later'
                ? T('Utsatte saker')
                : T('Dette følger du opp')}
          </h2>
          <p data-testid="review-sub">
            {bucket === 'waiting'
              ? T('Disse krever ikke svaret ditt ennå.')
              : bucket === 'later'
                ? T('Velg en sak for å ta den opp igjen.')
                : // a read that failed or has not answered is not an all-clear (RF1)
                  !total && known === 'failed'
                  ? T('Kunne ikke hente alle sakene')
                  : !total && (p.loading || known === 'loading')
                    ? T('Henter saker …')
                    : T('Velg arbeidsområde')}
          </p>
        </div>
        <div className={css.vtabs} role="tablist" aria-label={T('Oppfølging')}>
          {(
            [
              ['active', T('Å følge opp'), mine.length],
              ['waiting', T('Venter'), p.waiting.length],
              ['later', T('Utsatt'), postponed.length],
            ] as [Bucket, string, number][]
          ).map(([b, label, n]) => (
            <button
              key={b}
              type="button"
              role="tab"
              aria-selected={bucket === b}
              data-testid={`review-bucket-${b}`}
              onClick={() => pickBucket(b)}
            >
              {label} <span>{n}</span>
            </button>
          ))}
        </div>
        <label className={css.search}>
          <Icon name="search" size={18} />
          <input
            type="search"
            autoComplete="off"
            placeholder={T('Søk etter person eller sak')}
            aria-label={T('Søk i saker')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="review-search"
          />
        </label>
        {search ? (
          found.length ? (
            <>
              <p className={css.results}>{T('{n} treff', { n: found.length })}</p>
              <ul className={css.rows}>{found.map((c) => row(c, true))}</ul>
            </>
          ) : (
            empty(T('Ingen treff'), T('Prøv et annet navn eller en sakstype.'))
          )
        ) : (
          <>
            {bucket === 'active' && (
              <section className={css.urgent} aria-label={T('Haster nå')} data-testid="review-urgent">
                <div className={css.urgentHead}>
                  <h3>{T('Haster nå')}</h3>
                  <p>{T('Pågår eller starter innen 24 timer')}</p>
                </div>
                {urg.length ? (
                  <ul className={css.rows}>{(allUrgent ? urg : urg.slice(0, 4)).map((c) => row(c, true))}</ul>
                ) : (
                  <p className={css.none}>{T('Ingenting haster nå.')}</p>
                )}
                {urg.length > 4 && !allUrgent && (
                  <button
                    type="button"
                    className={css.textLink}
                    data-testid="review-urgent-all"
                    onClick={() => setAllUrgent(true)}
                  >
                    {T('Vis alle {n}', { n: urg.length })}
                  </button>
                )}
              </section>
            )}
            {bucket === 'active' && areas.length > 0 && <p className={css.areas}>{T('Arbeidsområder')}</p>}
            {areas.map((a) => {
              const group = pool.filter((c) => c.cat === a.c);
              const nUrgent = bucket === 'active' ? group.filter((c) => urgent(c, now)).length : 0;
              const g = group.length ? null : gapOf(a.c);
              return (
                <button
                  key={a.c}
                  type="button"
                  className={css.catRow}
                  data-testid={`review-tab-${a.c}`}
                  onClick={() => openList({ kind: 'cat', cat: a.c })}
                >
                  <span className={css.catIcon}>
                    <Icon name={a.icon} size={20} />
                  </span>
                  <span className={css.catCopy}>
                    <b>{T(a.name)}</b>
                    <span>
                      {bucket === 'waiting'
                        ? T('Venter på svar fra ansatte')
                        : bucket === 'later'
                          ? T('Kommer tilbake til oppfølging')
                          : T(a.sub)}
                    </span>
                  </span>
                  <span className={css.catEnd}>
                    {nUrgent > 0 && <span className={css.nUrgent}>{T('{n} haster', { n: nUrgent })}</span>}
                    {/* an area whose reads have not all answered is not known clear: ? or … (RF1) */}
                    <span className={css.catCount}>{g === 'failed' ? '?' : g ? '…' : group.length}</span>
                  </span>
                  <Icon name="chevronRight" size={16} />
                </button>
              );
            })}
            {!areas.length &&
              (bucket === 'later'
                ? empty(T('Ingen utsatte saker'), T('Saker du tar senere, samles her.'))
                : bucket === 'waiting'
                  ? empty(T('Ingen venter på andre'), T('Du finner de andre sakene i fanene over.'))
                  : !p.loading && !known
                    ? empty(T('Alt er fulgt opp'), T('Du finner de andre sakene i fanene over.'))
                    : null)}
          </>
        )}
        {(p.elsewhere ?? 0) > 0 && bucket === 'active' && (
          <button
            type="button"
            className={css.textLink}
            onClick={p.onElsewhere}
            data-testid="review-elsewhere"
          >
            {p.elsewhere === 1
              ? T('1 sak i andre perioder')
              : T('{n} saker i andre perioder', { n: p.elsewhere ?? 0 })}
            <Icon name="chevronRight" size={16} />
          </button>
        )}
      </>
    );
  };

  const listView = (l: List) => {
    const ask =
      p.onAskHugin && l.kind === 'cat' && (l.cat === 'planlegg' || l.cat === 'timer' || l.cat === 'lonn');
    return (
      <>
        <div className={css.vhead}>
          <button
            type="button"
            className={css.crumb}
            onClick={() => openList(null)}
            data-testid="review-back"
          >
            <Icon name="chevronLeft" size={16} /> {T('Alle saker')}
          </button>
          <h2 ref={headRef} tabIndex={-1}>
            {listName} <span className={css.titleCount}>{shown.length}</span>
          </h2>
          {l.kind === 'scope' ? (
            !shown.length && (
              <p data-testid="review-scope">
                {T('Sakene for {label} er løst eller ikke lenger åpne.', { label: l.label })}
              </p>
            )
          ) : (
            <p>
              {bucket === 'waiting'
                ? T('Venter på ansatte')
                : bucket === 'later'
                  ? T('Utsatte saker')
                  : T(AREAS.find((a) => a.c === l.cat)?.sub ?? '')}
            </p>
          )}
          {ask && (
            <button
              type="button"
              className={css.textLink}
              onClick={() =>
                p.onAskHugin?.(
                  T('Se på sakene i «{g}» i perioden og foreslå løsninger etter reglene.', { g: listName }),
                )
              }
            >
              <Icon name="sparkle" size={14} /> {T('La Hugin foreslå løsninger')}
            </button>
          )}
        </div>
        {shown.length ? (
          <ul className={css.rows}>{shown.map((c) => row(c, l.kind === 'scope', l))}</ul>
        ) : (
          l.kind === 'cat' && empty(T('Ingen saker her'), T('Gå tilbake for å se de andre arbeidsområdene.'))
        )}
      </>
    );
  };

  const detail = () => (
    <div className={css.detail} data-testid="review-case" data-key={active?.key}>
      <div className={css.vnav}>
        <button type="button" className={css.crumb} onClick={back} disabled={busy} data-testid="review-back">
          <Icon name="chevronLeft" size={16} /> {listName}
        </button>
        {active && at >= 0 && (
          <div className={css.pager}>
            <button
              type="button"
              aria-label={T('Forrige sak')}
              aria-keyshortcuts="K"
              disabled={busy || at <= 0}
              onClick={() => step(-1)}
            >
              <Icon name="chevronLeft" size={16} />
            </button>
            <span className={css.position} data-testid="review-selector">
              {T('{i} av {n}', { i: at + 1, n: shown.length })}
            </span>
            <button
              type="button"
              aria-label={T('Neste sak')}
              aria-keyshortcuts="J"
              disabled={busy || at >= shown.length - 1}
              onClick={() => step(1)}
            >
              <Icon name="chevronRight" size={16} />
            </button>
          </div>
        )}
      </div>
      {parked ? (
        <p className={css.incomplete} data-testid="review-parked" aria-live="polite">
          <Icon name="user" size={16} />
          <span>
            {T('Velger erstatter: {t}. Saken venter til du har valgt eller lukket panelet.', { t: parked })}
          </span>
        </p>
      ) : active ? (
        <CaseDetail
          c={active}
          busy={busy}
          error={error}
          titleRef={titleRef}
          outside={p.outside(active)}
          waiting={bucket === 'waiting' || p.waiting.includes(active)}
          later={laterSet.has(active.key)}
          impact={p.impact ? p.impact(active) : null}
          extra={p.extra ? p.extra(active) : null}
          about={p.about ? p.about(active) : null}
          onPerson={p.onPerson}
          onDay={p.onDay}
          onShowPeriod={() => p.onShowPeriod(active)}
          onAction={(a) => {
            if (a.kind === 'link') p.onLink(a.to);
            else if (a.kind === 'open') p.onOpen(a.item);
            else if (a.kind === 'shift') p.onShift?.(a.shiftId);
            else if (a.kind === 'person') p.onPerson(a.id);
            else if (a.kind !== 'contact') void act(active, a);
          }}
          onSubmit={(a, input) => void act(active, a, input)}
          onLater={() => onLater(active)}
          onDirty={onDirty}
        />
      ) : (
        <p className={css.none} aria-live="polite">
          {T('Saken er løst eller endret et annet sted.')}{' '}
          {shown[0] && (
            <button type="button" className={css.textLink} onClick={() => go((shown[0] as ReviewCase).key)}>
              {T('Gå til neste sak')}
            </button>
          )}
        </p>
      )}
    </div>
  );

  const levels = (
    <>
      {failedGaps.length > 0 && (
        <div className={css.incomplete} data-testid="review-incomplete">
          <Icon name="warn" size={16} />
          <span>
            {T('Kunne ikke hente {what}. Tallene kan være for lave.', {
              what: failedGaps.map((g) => g.label).join(', '),
            })}
          </span>
          <button type="button" className={css.small} onClick={p.onRetry}>
            {T('Prøv igjen')}
          </button>
        </div>
      )}
      {active || gone ? detail() : list ? listView(list) : overview()}
      {receipt && (
        <div className={css.receipt} data-testid="review-receipt" aria-live="polite">
          <Icon name="check" size={18} strokeWidth={1.7} />
          <span>{`${receipt.text} ${receipt.message}`}</span>
          <button
            type="button"
            className={css.small}
            aria-label={T('Skjul')}
            onClick={() => setReceipt(null)}
          >
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
    </>
  );

  if (panel)
    return (
      <section ref={rootRef} className={css.nav3} data-testid="review" aria-label={T('Saker')}>
        {levels}
      </section>
    );

  // the phone: the count and what it covers, and the same levels in a sheet
  const clear = total === 0 && !p.loading && !known;
  return (
    <section ref={rootRef} className={css.root} data-testid="review" aria-label={T('Saker å se over')}>
      <div className={`${css.bar} ${clear ? css.clear : ''}`}>
        <div className={css.summary}>
          <Icon name={clear ? 'check' : 'warn'} size={18} strokeWidth={1.7} />
          <div className={css.summaryText}>
            <strong data-testid="review-total">
              {total
                ? count(total)
                : known === 'failed'
                  ? T('Kunne ikke hente alle sakene')
                  : p.loading || known === 'loading'
                    ? T('Henter saker …')
                    : T('Ingenting å se over')}
            </strong>
            {total > 0 && <span className={css.scope}>{p.scope}</span>}
            {total > 0 && <p>{p.breakdown}</p>}
          </div>
          {(total > 0 || p.waiting.length > 0) && (
            <button
              type="button"
              className={css.link}
              onClick={() => {
                setOpen(true);
                setList(null);
                go(null);
              }}
              data-testid="review-open"
            >
              {T('Vis saker')} <Icon name="chevronRight" size={18} />
            </button>
          )}
        </div>
      </div>
      <Drawer
        open={open}
        onClose={() => {
          if (busyRef.current) return;
          // nothing half done is kept; closing resolves nothing (UC11)
          reset();
          setOpen(false);
          setActiveKey(null);
          setGone(null);
        }}
        title={T('Saker')}
      >
        <div className={`${css.nav3} ${css.sheet}`}>{levels}</div>
      </Drawer>
    </section>
  );
}
